import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTimer } from '../../hooks/useTimer';
import Button from '../ui/Button';
import { FiPlay, FiPause, FiSquare, FiActivity, FiStar, FiClock } from 'react-icons/fi';

const MOCK_ACTIVITIES = [
  { id: 1, user: 'Ahmet Y.', action: '45dk Matematik çalıştı', icon: <FiClock />, color: 'var(--color-info)' },
  { id: 2, user: 'Zeynep K.', action: '7 günlük çalışma serisine ulaştı! 🔥', icon: <FiActivity />, color: 'var(--color-warning)' },
  { id: 3, user: 'Can D.', action: 'Günün görevini tamamladı', icon: <FiStar />, color: 'var(--color-success)' },
  { id: 4, user: 'Elif S.', action: 'Liderlik tablosunda 2. sıraya yükseldi', icon: <FiActivity />, color: 'var(--accent-primary)' },
];

const MockGlobalTimer = () => {
  const [hours, setHours] = useState(12405);
  const [minutes, setMinutes] = useState(42);
  const [seconds, setSeconds] = useState(15);

  useEffect(() => {
    const interval = setInterval(() => {
      setSeconds(s => {
        if (s === 59) {
          setMinutes(m => {
            if (m === 59) {
              setHours(h => h + 1);
              return 0;
            }
            return m + 1;
          });
          return 0;
        }
        return s + 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <motion.div 
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: 'var(--space-2) var(--space-4)',
        background: 'rgba(255, 255, 255, 0.03)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(10px)',
        marginBottom: 'var(--space-6)'
      }}
    >
      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
        Yelkencilerin Toplam Odaklanma Süresi
      </span>
      <span style={{ 
        fontFamily: 'var(--font-mono)', 
        fontSize: '1.5rem', 
        fontWeight: 700, 
        color: 'var(--accent-primary)',
        textShadow: '0 0 15px rgba(16, 185, 129, 0.4)',
        fontVariantNumeric: 'tabular-nums'
      }}>
        {hours.toLocaleString()}:{minutes.toString().padStart(2, '0')}:{seconds.toString().padStart(2, '0')}
      </span>
    </motion.div>
  );
};

/**
 * Enhanced Hero section — guest users see headline, timer, and live activity feed.
 * When timer runs, session data is stored in localStorage.
 * On stop, a CTA prompts registration.
 */
export default function HeroSection({ onAuthOpen }) {
  const { chronoTime, chronoRunning, chronoToggle, chronoReset, formatTime } = useTimer('chronometer');
  const [sessionStarted, setSessionStarted] = useState(false);
  const [showSignupCTA, setShowSignupCTA] = useState(false);
  const [currentActivityIndex, setCurrentActivityIndex] = useState(0);

  // Cycle through mock activities
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentActivityIndex((prev) => (prev + 1) % MOCK_ACTIVITIES.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const handleStartClick = () => {
    // Redirect to study-time page with autoStart true
    window.location.href = '/study-time'; // Using window.location since useNavigate is not imported here, wait let me check if I can use useNavigate. HeroSection doesn't have it. We can just use window.location. Or I can import useNavigate. Let's use window.location for simplicity or import it.
  };

  return (
    <section className="hero">
      {/* Gradient blobs */}
      <div className="gradient-blob gradient-blob--indigo animate-blob" style={{ width: 500, height: 500, top: '-20%', left: '-10%' }} />
      <div className="gradient-blob gradient-blob--violet animate-blob delay-3" style={{ width: 400, height: 400, bottom: '-10%', right: '-5%' }} />

      <div className="hero-content" style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
        <MockGlobalTimer />
        <motion.h1
          className="hero-title"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          Hedefine{' '}
          <span className="text-gradient">Odaklan.</span>
        </motion.h1>

        <motion.p
          className="hero-subtitle"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          Çalışma sürenizi takip edin, hedeflerinize ulaşın, topluluğumuzla birlikte ilerleyin.
        </motion.p>

        {/* Live Activity Feed (Mock) */}
        <motion.div
          className="hero-live-feed"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '10px',
            background: 'var(--bg-glass-strong)',
            padding: '8px 16px',
            borderRadius: 'var(--radius-full)',
            border: '1px solid var(--border-subtle)',
            marginBottom: 'var(--space-6)',
            overflow: 'hidden',
            minWidth: '300px',
            justifyContent: 'center'
          }}
        >
          <span className="live-indicator"></span>
          <AnimatePresence mode="wait">
            <motion.div
              key={currentActivityIndex}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}
            >
              <span style={{ color: MOCK_ACTIVITIES[currentActivityIndex].color }}>
                {MOCK_ACTIVITIES[currentActivityIndex].icon}
              </span>
              <strong style={{ color: 'var(--text-primary)' }}>{MOCK_ACTIVITIES[currentActivityIndex].user}</strong>
              <span>{MOCK_ACTIVITIES[currentActivityIndex].action}</span>
            </motion.div>
          </AnimatePresence>
        </motion.div>

        {/* Seamless Giant Clock Timer */}
        <motion.div
          className="hero-timer seamless-timer"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          style={{
            margin: '0 auto',
            width: '100%',
            marginBottom: 'var(--space-8)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative'
          }}
        >
          <div style={{ zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', width: '100%' }}>
            <div style={{ 
              fontFamily: 'var(--font-mono)', 
              fontWeight: 200, 
              fontSize: 'clamp(4rem, 16vw, 12rem)', 
              color: 'var(--text-primary)', 
              letterSpacing: '-0.02em', 
              display: 'flex', 
              alignItems: 'baseline', 
              justifyContent: 'center',
              textShadow: '0 0 60px rgba(16, 185, 129, 0.2)',
              marginBottom: 'var(--space-4)',
              width: '100%',
              lineHeight: 1
            }}>
              <span>00:00:00</span>
              <span style={{ fontSize: 'clamp(2rem, 8vw, 5rem)', marginLeft: '8px', color: 'var(--text-muted)' }}>.0</span>
            </div>
            
            <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-6)', maxWidth: '400px', fontSize: 'var(--text-lg)' }}>
              Odaklanmaya hazır mısın? Süreyi başlat ve çalışma sayfasına geç.
            </p>
            
            <Button 
              variant="primary" 
              size="xl" 
              onClick={() => window.location.href = '/study-time'} 
              icon={<FiPlay size={22} />}
              style={{ 
                fontSize: '1.2rem', padding: '16px 40px', borderRadius: 'var(--radius-full)',
                background: 'linear-gradient(90deg, #10b981, #059669)',
                boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)'
              }}
            >
              Süreyi Başlat
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
